import React from 'react';

/**
 * Derives the intended pixel size from a Tailwind className like "h-5 w-5".
 * Tailwind spacing: h-N = N * 4px (h-5 = 20px, h-6 = 24px, h-7 = 28px, h-8 = 32px).
 * Returns null when no h-N class is present.
 */
function sizeFromClass(cls?: string): number | null {
  const m = cls?.match(/\bh-(\d+)\b/);
  return m ? parseInt(m[1], 10) * 4 : null;
}

/**
 * Whistle icon using the HUSTLi transparent-background logo asset.
 *
 * The PNG has a small amount of inherent padding around the whistle shape, so
 * we render at 1.4× the intended size and apply a negative margin to keep the
 * visual weight matching adjacent Lucide icons without shifting layout.
 *
 * Accepts both Tailwind `className` (e.g. "h-5 w-5", "h-6 w-6") and an
 * explicit `size` prop so it works in all three contexts:
 *   - Sidebar / MobileNav  →  className="h-5 w-5"  (20 px)
 *   - TeamHub tiles        →  className="h-6 w-6"  (24 px)
 *   - PageHeader           →  size={24}             (24 px)
 */
const WhistleIcon: React.FC<{ size?: number; className?: string; 'aria-hidden'?: boolean }> = ({
  size = 24,
  className,
  'aria-hidden': ariaHidden,
}) => {
  const effectiveSize = sizeFromClass(className) ?? size;
  const renderSize = Math.round(effectiveSize * 1.4);
  const margin = -Math.round((renderSize - effectiveSize) / 2);

  return (
    <img
      src="/assets/images/hustli-logo-icon.png"
      alt=""
      aria-hidden={ariaHidden}
      style={{
        width: renderSize,
        height: renderSize,
        margin,
        display: 'block',
        flexShrink: 0,
        objectFit: 'contain',
        paddingBottom: 4,
      }}
    />
  );
};

export default WhistleIcon;
